import { PayloadAction } from '@reduxjs/toolkit';
import {
  all,
  call,
  fork,
  put,
  take,
  takeLatest,
} from 'redux-saga/effects';
import { eventChannel, EventChannel } from 'redux-saga';
import { chatbotApi } from '../api';
import { websocketService } from '@/shared/services/websocket.service';
import {
  sendMessageRequest,
  sendMessageSuccess,
  sendMessageFailure,
  receiveMessageChunk,
  receiveMessageComplete,
  createConversationRequest,
  createConversationSuccess,
  createConversationFailure,
  fetchConversationsRequest,
  fetchConversationsSuccess,
  fetchConversationsFailure,
  selectConversation,
  loadConversationMessagesRequest,
  loadConversationMessagesSuccess,
  loadConversationMessagesFailure,
  renameConversationRequest,
  renameConversationSuccess,
  renameConversationFailure,
  deleteConversationRequest,
  deleteConversationSuccess,
  deleteConversationFailure,
  setWebSocketConnected,
  openHistory,
} from './chatbot-slice';
import { ChatMessage } from '../types';

/**
 * Helper para obtener mensajes de error
 */
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as any;
    const response = axiosError.response;

    if (response?.data) {
      if (typeof response.data === 'string') {
        return response.data;
      }
      if (response.data.message) {
        return response.data.message;
      }
      if (response.data.errors && Array.isArray(response.data.errors)) {
        return response.data.errors
          .map((e: any) => `${e.field || ''}: ${e.message || e}`)
          .join(', ');
      }
    }

    return `Error ${response?.status || 'desconocido'}: ${response?.statusText || 'Error en la petición'}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Ha ocurrido un error inesperado';
};

/**
 * Crear canal de eventos para WebSocket del chatbot
 */
function createChatbotWebSocketChannel(): EventChannel<any> {
  return eventChannel((emitter) => {
    // Escuchar eventos de conexión
    const unsubscribeConnect = websocketService.on('connect', () => {
      emitter({ type: 'connected' });
    });

    const unsubscribeDisconnect = websocketService.on('disconnect', () => {
      emitter({ type: 'disconnected' });
    });

    // Escuchar eventos del chatbot
    const unsubscribeChatMessage = websocketService.on('chatbot:message', (data: any) => {
      emitter({
        type: 'message',
        data,
      });
    });

    const unsubscribeChatChunk = websocketService.on('chatbot:chunk', (data: any) => {
      emitter({
        type: 'chunk',
        data,
      });
    });

    const unsubscribeChatComplete = websocketService.on('chatbot:complete', (data: any) => {
      emitter({
        type: 'complete',
        data,
      });
    });

    // Función de limpieza
    return () => {
      unsubscribeConnect();
      unsubscribeDisconnect();
      unsubscribeChatMessage();
      unsubscribeChatChunk();
      unsubscribeChatComplete();
    };
  });
}

/**
 * Saga para manejar eventos WebSocket del chatbot
 */
function* handleChatbotWebSocket() {
  const channel: EventChannel<any> = yield call(createChatbotWebSocketChannel);

  try {
    // Conectar WebSocket si no está conectado
    if (!websocketService.getIsConnected()) {
      websocketService.connect();
    }

    yield put(setWebSocketConnected(true));

    while (true) {
      const event = yield take(channel);

      switch (event.type) {
        case 'connected':
          yield put(setWebSocketConnected(true));
          break;

        case 'disconnected':
          yield put(setWebSocketConnected(false));
          break;

        case 'chunk':
          // Recibir chunk de mensaje (streaming)
          yield put(
            receiveMessageChunk({
              chunk: event.data.chunk,
              conversationId: event.data.conversationId,
            }),
          );
          break;

        case 'complete':
          // Mensaje completo recibido
          yield put(
            receiveMessageComplete({
              message: event.data.message,
              conversationId: event.data.conversationId,
              timestamp: event.data.timestamp 
                ? (typeof event.data.timestamp === 'string' ? event.data.timestamp : new Date(event.data.timestamp).toISOString())
                : new Date().toISOString(),
              metadata: event.data.metadata,
            }),
          );
          break;

        case 'message':
          // Mensaje completo (no streaming)
          yield put(
            receiveMessageComplete({
              message: event.data.message,
              conversationId: event.data.conversationId,
              timestamp: event.data.timestamp 
                ? (typeof event.data.timestamp === 'string' ? event.data.timestamp : new Date(event.data.timestamp).toISOString())
                : new Date().toISOString(),
              metadata: event.data.metadata,
            }),
          );
          break;
      }
    }
  } catch (error) {
    yield put(setWebSocketConnected(false));
  } finally {
    channel.close();
  }
}

/**
 * Saga para enviar mensaje
 */
function* sendMessageSaga(action: PayloadAction<{ message: string; conversationId?: string }>) {
  try {
    const response = yield call(chatbotApi.sendMessage, {
      message: action.payload.message,
      conversationId: action.payload.conversationId,
    });

    yield put(
      sendMessageSuccess({
        message: response.message,
        conversationId: response.conversationId,
        timestamp: response.timestamp instanceof Date 
          ? response.timestamp.toISOString() 
          : typeof response.timestamp === 'string' 
            ? response.timestamp 
            : new Date(response.timestamp).toISOString(),
        metadata: response.metadata,
      }),
    );

    // Si hay WebSocket conectado, también escuchar por streaming
    if (websocketService.getIsConnected()) {
      // El WebSocket manejará los chunks automáticamente
    }
  } catch (error) {
    yield put(sendMessageFailure(getErrorMessage(error)));
  }
}

/**
 * Saga para crear conversación
 */
function* createConversationSaga() {
  try {
    const conversation = yield call(chatbotApi.createConversation);
    
    yield put(
      createConversationSuccess({
        conversationId: conversation.id || conversation.conversationId || conversation.conversationId,
        title: conversation.title || conversation.name || 'Nueva Conversación',
      }),
    );
  } catch (error) {
    yield put(createConversationFailure(getErrorMessage(error)));
  }
}

/**
 * Saga para obtener historial de conversaciones
 */
function* fetchConversationsSaga(
  action: PayloadAction<{ page?: number; limit?: number; search?: string } | undefined>,
) {
  try {
    const params = action.payload || {};
    const response = yield call(
      chatbotApi.getConversations,
      params.page || 1,
      params.limit || 10,
      params.search,
    );

    yield put(fetchConversationsSuccess(response));
  } catch (error) {
    yield put(fetchConversationsFailure(getErrorMessage(error)));
  }
}

/**
 * Saga para cargar mensajes de una conversación
 */
function* loadConversationMessagesSaga(action: PayloadAction<string>) {
  try {
    const conversationId = action.payload;
    const messages = yield call(chatbotApi.getConversationHistory, conversationId);

    // Convertir a formato ChatMessage
    const chatMessages: ChatMessage[] = messages.map((msg: any) => ({
      id: msg.id,
      content: msg.content,
      role: msg.role,
      timestamp: msg.timestamp 
        ? (msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date(msg.timestamp).toISOString())
        : (msg.createdAt instanceof Date ? msg.createdAt.toISOString() : new Date(msg.createdAt).toISOString()),
    }));

    yield put(loadConversationMessagesSuccess(chatMessages));
  } catch (error) {
    yield put(loadConversationMessagesFailure(getErrorMessage(error)));
  }
}

/**
 * Saga para seleccionar conversación
 */
function* selectConversationSaga(action: PayloadAction<any>) {
  const conversation = action.payload;
  
  // Cargar mensajes de la conversación
  yield put(loadConversationMessagesRequest(conversation.id));
  
  try {
    const messages = yield call(chatbotApi.getConversationHistory, conversation.id);
    
    const chatMessages: ChatMessage[] = messages.map((msg: any) => ({
      id: msg.id,
      content: msg.content,
      role: msg.role,
      timestamp: msg.timestamp 
        ? (msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date(msg.timestamp).toISOString())
        : (msg.createdAt instanceof Date ? msg.createdAt.toISOString() : new Date(msg.createdAt).toISOString()),
    }));

    yield put(loadConversationMessagesSuccess(chatMessages));
  } catch (error) {
    yield put(loadConversationMessagesFailure(getErrorMessage(error)));
  }
}

/**
 * Saga para renombrar conversación
 */
function* renameConversationSaga(
  action: PayloadAction<{ conversationId: string; newTitle: string }>,
) {
  try {
    yield call(chatbotApi.renameConversation, {
      conversationId: action.payload.conversationId,
      newTitle: action.payload.newTitle,
    });

    yield put(renameConversationSuccess(action.payload));
    
    // Refrescar lista de conversaciones
    yield put(fetchConversationsRequest(undefined));
  } catch (error) {
    yield put(
      renameConversationFailure({
        conversationId: action.payload.conversationId,
        error: getErrorMessage(error),
      }),
    );
  }
}

/**
 * Saga para eliminar conversación
 */
function* deleteConversationSaga(action: PayloadAction<string>) {
  try {
    yield call(chatbotApi.deleteConversation, action.payload);
    yield put(deleteConversationSuccess());
    
    // Refrescar lista de conversaciones
    yield put(fetchConversationsRequest(undefined));
  } catch (error) {
    yield put(deleteConversationFailure(getErrorMessage(error)));
  }
}

/**
 * Saga para abrir historial (cargar conversaciones)
 */
function* openHistorySaga() {
  yield put(fetchConversationsRequest(undefined));
}

/**
 * Saga principal del chatbot
 */
export function* chatbotSaga() {
  yield all([
    // Iniciar WebSocket listener
    fork(handleChatbotWebSocket),
    
    // Message actions
    takeLatest(sendMessageRequest.type, sendMessageSaga),
    
    // Conversation actions
    takeLatest(createConversationRequest.type, createConversationSaga),
    
    // History actions
    takeLatest(fetchConversationsRequest.type, fetchConversationsSaga),
    takeLatest(loadConversationMessagesRequest.type, loadConversationMessagesSaga),
    takeLatest(selectConversation.type, selectConversationSaga),
    takeLatest(renameConversationRequest.type, renameConversationSaga),
    takeLatest(deleteConversationRequest.type, deleteConversationSaga),
    
    // UI actions
    takeLatest(openHistory.type, openHistorySaga),
  ]);
}

